import { sv } from "@dhx/ts-common/dom";

const defaultColors = [
	"#394E79",
	"#5E83BA",
	"#C2D2E9",
	"#647B37",
	"#98A468",
	"#F0D0A9",
	"#EEB98E",
	"#9A8BA5",
	"#E3C5D5"
];

export function getDefaultColor(index: number = 0): string {
	return defaultColors[index];
}

type Locator = (data: any) => number | string;

export function locator(value : string|Locator) : Locator{
	if (!value){
		return () => "";
	}

	if (typeof value === "string"){
		return obj => obj[value];
	} else {
		return value;
	}
}

export function log10(x: number): number {
	return Math.log(x) / Math.LN10;
}

export const getTextWidth = anyArgsMemo((text: string, font: string = ""): number => {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	if (font) {
		ctx.font = font;
	}
	return ctx.measureText(text).width;
});

function anyArgsMemo<U>(fn: (...args: string[]) => U): (...args: string[]) => U {
	const cached = {};

	return (...args) => {
		let mem = cached;
		for (let i=0; i<args.length-1; i++) {
			mem[args[i]] = mem[args[i]] || {};
			mem = mem[args[i]];
		}
		const last = args.length - 1;
		if (mem[last]) {
			return mem[last];
		}
		return mem[last] = fn(...args);
	};
}

function memo<U>(fn: (arg: string) => U): (arg: string) => U {
	const cached = {};

	return (arg) => {
		if (cached[arg]) {
			return cached[arg];
		}
		return cached[arg] = fn(arg);
	};
}

function getRgbaFromColor(color: string): [number, number, number] {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	ctx.fillStyle = color;
	ctx.fillRect(0,0,2,2);
	const rgba = ctx.getImageData(1, 1, 1, 1).data;
	return [rgba[0], rgba[1], rgba[2]];
}

const memoizedColorFromRgba = memo(getRgbaFromColor);
export function getColorShade(color: string, light: number): string {
	const [r, g, b] = memoizedColorFromRgba(color).map(value => Math.floor(value * light + 255 * (1 - light)));
	return `rgb(${r},${g},${b})`;
}
export const getFontStyle = memo((className: string): string => {
	const chart: SVGElement = document.createElementNS("http://www.w3.org/2000/svg", "svg");
	chart.setAttribute("class", "dhx_chart");
	const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
	text.setAttribute("class", className);

	chart.setAttribute("visibility", "hidden");
	text.textContent = "test";
	chart.appendChild(text);
	document.body.appendChild(chart);
	const style = getComputedStyle(text);
	const font = `${style.fontSize} ${style.fontFamily}`;
	document.body.removeChild(chart);
	return font;
});


interface IStop {
	offset: number;
	color: string;
	opacity?: number;
}
interface IGradient {
	stops: IStop[];
}
export function linearGradient(grad: IGradient, id: string) {
	const stops = grad.stops;
	const colors = stops.map(item => sv("stop", {"offset": `${item.offset * 100}%`, "stop-color": item.color, "stop-opacity": item.opacity || 1}));
	const gradient = sv("linearGradient", {
		id,
		gradientTransform: "rotate(90)"
	}, colors);
	return gradient;
}

export function getRadialGradient(opts: any, stops: IStop[], id: string) {
	const colors = stops.map(item => sv("stop", {"offset": item.offset, "stop-color": item.color, "stop-opacity": item.opacity || 1}));
	const gradient = sv("radialGradient", {id, cx: 0, cy: 0, gradientUnits: "userSpaceOnUse", ...opts}, colors);
	return gradient;
}

export function euclideanDistance(x1: number, y1: number, x2: number, y2: number): number {
	return Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));
}

export function verticalCenteredText(text: string) {
	return sv("tspan", {
		dy: "0.5ex"
	}, text);
}

export function verticalTopText(text: string) {
	return sv("tspan", {
		dy: "-0.5ex"
	}, text);
}

export function verticalBottomText(text: string) {
	return sv("tspan", {
		dy: "1.5ex"
	}, text);
}

export function calcPointRef(pointId: string, serieId: string): string {
	return pointId + "_" + serieId;
}